import {
  Component,
  ViewEncapsulation,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Project
import { InsertMediaService } from '../../../services/insert-media.service';
import { ImageMediaProperties } from '../../../types';
import { ALLOW_FOR_NUMBER_INPUT } from './wk-media-properties.constants';
import { WkInfodrawerService } from '../services/wk-info-drawer.service';
import { MediaTypes } from '../../../enums';

@Component({
  selector: 'wk-media-properties',
  templateUrl: './wk-media-properties.component.html',
  styleUrls: ['./wk-media-properties.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkMediaPropertiesComponent implements OnInit, OnDestroy {
  @Input() editable;
  @Input() mediaType;
  disableAlignment = false;
  mediaExpandProperties = false;
  isMediaProperties = false;
  alignmentSelected = 'Left';
  imageName = '';
  imageAlternativeText = '';
  imageWidth;
  imageHeight;
  mediaTitle;
  mediaName;
  mediaDesc;
  mediaCredLine;
  alignmentGroup = ['None', 'left', 'center', 'right'];
  enableLock = false;
  ratio;
  restoreProperties;
  onDestroy$ = new Subject<boolean>();
  id: any;
  imageId: any;
  mediaId: any;
  images: any[] = [];
  mediaTenantId: any;
  imageTenantId: any;
  isInvalid: boolean;
  isInvalidAltText = false;
  restoreMediaDescription: string;
  restoreMediaCreditLine: string;
  private defaultHeightWidth: ImageMediaProperties;
  @ViewChild('altTextarea') altTextarea!: ElementRef<HTMLTextAreaElement>;

  public mediaImage = MediaTypes.Img.toLocaleUpperCase();
  constructor(
    private readonly insertMediaService: InsertMediaService,
    private readonly wkInfodrawerService: WkInfodrawerService,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit() {
    this.insertMediaService
      .getDisable()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this.disableAlignment = data;
      });
    this.insertMediaService
      .getMediaProperties()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        if (data) {
          if (data.mediaId) {
            this.mediaElement(data);
          }
          this.imageId = data.id;
          this.imageElement(data);
        }
      });
    this.wkInfodrawerService
      .getCancelEvent()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.restore('discardChanges');
      });
    this.insertMediaService
      .getUpdatedMediaProperties()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        if (!data) {
          this.clearValue();
        }
      });
    this.insertMediaService
      .getMediaElement()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        if (!data) {
          this.clearMediaValue();
        }
      });

    this.insertMediaService
      .getSaveForMedia()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: boolean) => {
        if (data) {
          this.updateMediaProperties();
          this.restoreMediaDescription = this.mediaDesc;
          this.restoreMediaCreditLine = this.mediaCredLine;
        }
      });
    this.images = JSON.parse(localStorage.getItem('imageId')) || [];
  }

  updateMediaProperties() {
    if (this.isInvalidAltText) {
      this.isInvalidAltText = false;
      this.imageAlternativeText = this.imageName;
    }
    if (this.isInvalid) {
      this.isInvalid = false;
      this.imageHeight = this.defaultHeightWidth.height;
      this.imageWidth = this.defaultHeightWidth.width;
    }
  }

  enableLockIcon() {
    if (this.images.length > 0) {
      if (!this.images.includes(this.imageId)) {
        this.images.push(this.imageId);
        this.enableLock = true;
      } else {
        const index = this.images.indexOf(this.imageId);
        this.images.splice(index, 1);
        this.enableLock = false;
      }
    } else {
      this.images.push(this.imageId);
      this.enableLock = true;
    }
    localStorage.setItem('imageId', JSON.stringify(this.images));
  }

  imageElement(data) {
    if (!data || !data.id) {
      return;
    }

    this.restoreProperties = data;
    this.imageTenantId = data.dataTenantId;
    this.imageAlternativeText = data.altText;

    setTimeout(() => {
      this.resizeAltTextarea();
    });

    this.imageName = data.name;
    this.imageHeight = data.height;
    this.imageWidth = data.width;
    this.ratio = this.imageWidth / this.imageHeight;
    this.id = data.id;
    const isValid = this.imageHeight && this.imageWidth;
    this.defaultHeightWidth = {
      height: data.naturalHeight,
      width: data.naturalWidth,
    };
    this.checkValid(isValid);
    this.isInvalidAltText = this.imageAlternativeText ? false : true;
    if (this.images.length > 0) {
      this.enableLock = this.images.includes(this.imageId) ? true : false;
    }
    if (data.align) {
      this.alignmentSelected =
        data.align === 'unset' ? 'None' : data.align.toLowerCase();
    } else {
      this.alignmentSelected = 'center';
    }
  }

  public capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  changeWidth(mediaWidth: number) {
    if (!this.enableLock) {
      const height = Math.round(mediaWidth / this.ratio);
      this.imageHeight = height;
      this.changeProperties('img');
    }
    this.changeProperties('img');
    this.checkValid(mediaWidth);
  }

  changeHeight(mediaHeight: number) {
    if (!this.enableLock) {
      const width = Math.round(mediaHeight * this.ratio);
      this.imageWidth = width;
    }
    this.changeProperties('img');
    this.checkValid(mediaHeight);
  }

  private checkValid(imageProperity) {
    this.isInvalid = !imageProperity ? true : false;
  }

  allowNumbersOnly(event) {
    if (
      event &&
      event.key &&
      !ALLOW_FOR_NUMBER_INPUT.find((num) => num === event.key)
    ) {
      event.preventDefault();
    }
  }

  changeAlignment(alignmentSelected: string): void {
    this.alignmentSelected = alignmentSelected;
    this.changeProperties('img');
  }

  restore(val) {
    if (this.restoreProperties?.mediaId) {
      this.restoreMediaProperties(val);
    }
    if (this.restoreProperties?.id) {
      this.restoreImageProperties(val);
    }
  }

  restoreImageProperties(val: string) {
    let changePropFlag = true;
    const { naturalWidth, naturalHeight, width, height, altText, align } =
      this.restoreProperties;
    if (
      this.imageWidth === (naturalWidth ?? width) &&
      this.imageHeight === (naturalHeight ?? height) &&
      val !== 'discardChanges'
    ) {
      changePropFlag = false;
    }
    this.imageWidth = naturalWidth ?? width;
    this.imageHeight = naturalHeight ?? height;
    const isValid = this.imageHeight && this.imageWidth;
    this.checkValid(isValid);
    if (val === 'discardChanges') {
      this.imageAlternativeText = altText;
      this.isInvalidAltText = this.imageAlternativeText ? false : true;
      this.alignmentSelected = align ?? 'Left';
    }
    changePropFlag && this.changeProperties('img');
  }

  changeProperties(val) {
    if (val === 'img' && this.imageName.length > 1) {
      this.insertMediaService.buttonEnable.next(true);
      this.insertMediaService.setUpdatedImageProperties({
        value: {
          imageTenantId: this.imageTenantId,
          imageWidth: this.imageWidth || this.defaultHeightWidth.width,
          imageHeight: this.imageHeight || this.defaultHeightWidth.height,
          imageAlignment:
            this.alignmentSelected === 'None'
              ? 'unset'
              : this.alignmentSelected,
          imageAlternativeText:
            this.imageAlternativeText === ''
              ? this.imageName
              : this.imageAlternativeText,
          id: this.id,
        },
      });
    }
    if (val === 'media' && this.mediaName.length > 1) {
      this.insertMediaService.buttonEnable.next(true);
      this.insertMediaService.setUpdatedMediaProperties({
        value: {
          mediaTenantId: this.mediaTenantId,
          title: this.mediaTitle,
          name: this.mediaName,
          desc: this.mediaDesc,
          credLine: this.mediaCredLine,
          mediaId: this.mediaId,
        },
      });
    }
  }

  changeAltTxt(changeAltTxt: string) {
    this.imageAlternativeText = changeAltTxt;
    this.changeProperties('img');
    this.isInvalidAltText = !changeAltTxt ? true : false;

    setTimeout(() => {
      this.resizeAltTextarea();
    });
  }

  clearValue() {
    this.imageHeight = '';
    this.imageWidth = '';
    this.imageName = '';
    this.imageAlternativeText = '';
    this.alignmentSelected = 'Left';
  }

  mediaElement(data) {
    this.restoreProperties = data;
    this.mediaTitle = data.title;
    this.mediaName = data.name;
    this.mediaDesc = data.desc;
    this.mediaCredLine = data.credLine;
    this.mediaId = data.mediaId;
    this.mediaTenantId = data.dataTenantId;
    this.restoreMediaDescription = data.desc;
    this.restoreMediaCreditLine = data.credLine;
  }

  changeMediaDesc(val) {
    this.mediaDesc = val;
    this.changeProperties('media');
  }

  changeMediaCredLine(val) {
    this.mediaCredLine = val;
    this.changeProperties('media');
  }

  clearMediaValue() {
    this.mediaTitle = '';
    this.mediaName = '';
    this.mediaDesc = '';
    this.mediaCredLine = '';
  }

  restoreMediaProperties(val) {
    let changePropFlagMedia = true;
    if (
      this.mediaDesc === this.restoreProperties.desc &&
      this.mediaCredLine === this.restoreProperties.credLine &&
      val !== 'discardChanges'
    ) {
      changePropFlagMedia = false;
    }
    if (val === 'discardChanges') {
      this.mediaDesc =
        this.restoreMediaDescription || this.restoreProperties.desc;
      this.mediaCredLine =
        this.restoreMediaCreditLine || this.restoreProperties.credLine;
    }
    if (changePropFlagMedia) {
      this.changeProperties('media');
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
    localStorage.removeItem('imageId');
  }

  private resizeAltTextarea(): void {
    if (!this.altTextarea) {
      return;
    }

    const el = this.altTextarea.nativeElement;

    this.renderer.setStyle(el, 'height', 'auto');
    const newHeight = Math.max(el.scrollHeight, 32) + 'px';
    this.renderer.setStyle(el, 'height', newHeight);
  }
}
